<template>
  <Teleport :to="store.overlayTarget">
    <!-- Backdrop -->
    <Transition name="fade">
      <div
        v-if="store.bookingSlideOverOpen"
        class="fixed inset-0 bg-black/20 backdrop-blur-[1px] z-30"
        @click="store.closeBookingSlideOver()"
      ></div>
    </Transition>

    <!-- Panel -->
    <Transition name="slide-over">
      <div
        v-if="store.bookingSlideOverOpen"
        class="fixed inset-y-0 right-0 w-full sm:w-[460px] bg-white dark:bg-[#1C2128] shadow-2xl z-40 flex flex-col"
        @click.stop
      >
        <BookingSlideOverHeader
          :title="isEditMode ? 'Edit Booking' : 'New Booking'"
          :saving="saving"
          @close="store.closeBookingSlideOver()"
          @save="submit"
        />

        <!-- Error banner -->
        <div v-if="error" class="mx-4 mt-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
          {{ error }}
        </div>

        <!-- Form body -->
        <div class="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          <BookingTypeToggle v-model="form.bookingType" />

          <!-- Internal booking fields -->
          <template v-if="form.bookingType === 'internal'">
            <BookingEventSelector
              v-model="form.eventId"
              :events="store.hostEvents"
              :loading="store.hostEventsLoading"
            />

            <div>
              <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Title</label>
              <input
                v-model="form.name"
                type="text"
                placeholder="Booking title (optional — uses event name)"
                class="w-full h-9 px-3 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <BookingDateTimeRow
              v-model:date="form.date"
              v-model:start-time="form.startTime"
              v-model:end-time="form.endTime"
            />

            <BookingGuestInput
              :guests="form.guests"
              @update:guests="form.guests = $event"
            />

            <div>
              <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</label>
              <textarea
                v-model="form.description"
                rows="3"
                placeholder="Add a note..."
                class="w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              ></textarea>
            </div>
          </template>

          <!-- External booking fields -->
          <template v-else>
            <div>
              <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Title</label>
              <input
                v-model="form.name"
                type="text"
                placeholder="Event title"
                class="w-full h-9 px-3 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <BookingDateTimeRow
              v-model:date="form.date"
              v-model:start-time="form.startTime"
              v-model:end-time="form.endTime"
            />

            <div>
              <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</label>
              <textarea
                v-model="form.description"
                rows="3"
                placeholder="Add a note..."
                class="w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              ></textarea>
            </div>

            <p class="text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800/60 rounded-lg px-3 py-2">
              Calendar events are personal reminders added to your Weekler calendar. They are not bookable by others.
            </p>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useCalendarStore } from 'stores/calendar.js'
import { useRequest }       from 'composables/useRequest.js'
import BookingSlideOverHeader from './BookingSlideOverHeader.vue'
import BookingTypeToggle      from './BookingTypeToggle.vue'
import BookingEventSelector   from './BookingEventSelector.vue'
import BookingDateTimeRow     from './BookingDateTimeRow.vue'
import BookingGuestInput      from './BookingGuestInput.vue'

// No props — reads entirely from store
const store = useCalendarStore()
const { axiosRequest } = useRequest()

const saving = ref(false)
const error  = ref(null)

const isEditMode = computed(() => !!store.bookingSlideOverBooking?.id)

// ── Form state ─────────────────────────────────────────────────────────────────

function defaultDate() {
  return new Date().toISOString().split('T')[0]
}

function defaultTime(offsetMinutes = 0) {
  const d = new Date()
  d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15 + offsetMinutes, 0, 0)
  return d.toTimeString().slice(0, 5)
}

const form = ref(blankForm())

function blankForm() {
  return {
    bookingType: 'internal',
    eventId:     '',
    name:        '',
    date:        defaultDate(),
    startTime:   defaultTime(0),
    endTime:     defaultTime(60),
    guests:      [],
    description: '',
  }
}

function buildFormFromBooking(booking) {
  const start = new Date(booking.start_at ?? booking.start)
  const end   = new Date(booking.end_at   ?? booking.end)
  return {
    bookingType: 'internal',
    eventId:     booking.event_id ?? '',
    name:        booking.title ?? booking.name ?? '',
    date:        start.toISOString().split('T')[0],
    startTime:   start.toTimeString().slice(0, 5),
    endTime:     end.toTimeString().slice(0, 5),
    guests:      [],
    description: booking.description ?? '',
  }
}

// Reset/populate form whenever the slide-over opens
watch(() => store.bookingSlideOverOpen, (open) => {
  if (!open) return
  error.value  = null
  saving.value = false
  form.value   = store.bookingSlideOverBooking
    ? buildFormFromBooking(store.bookingSlideOverBooking)
    : blankForm()
})

// ── Submit ─────────────────────────────────────────────────────────────────────

async function submit() {
  error.value = null

  if (form.value.bookingType === 'internal' && !form.value.eventId) {
    error.value = 'Please select an event type.'
    return
  }
  if (!form.value.date || !form.value.startTime || !form.value.endTime) {
    error.value = 'Please fill in the date and time.'
    return
  }
  if (form.value.endTime <= form.value.startTime) {
    error.value = 'End time must be after start time.'
    return
  }

  const startAt = `${form.value.date}T${form.value.startTime}:00`
  const endAt   = `${form.value.date}T${form.value.endTime}:00`

  saving.value = true

  try {
    if (form.value.bookingType === 'internal') {
      await submitInternal(startAt, endAt)
    } else {
      await submitExternal(startAt, endAt)
    }
  } finally {
    saving.value = false
  }
}

async function submitInternal(startAt, endAt) {
  const guests = form.value.guests.length
    ? form.value.guests
    : [{ email: 'host@placeholder.internal', full_name: 'Host' }]

  const res = await axiosRequest('POST', '/api/v1/calendar/internal_bookings', {
    booking: {
      event_id:              form.value.eventId,
      start_at:              startAt,
      end_at:                endAt,
      name:                  form.value.name || undefined,
      description:           form.value.description || undefined,
      booking_created_by_host: true,
    },
    attendees: guests,
  }, false)

  if (!res.js_error && res.response?.success) {
    store.closeBookingSlideOver()
    // ActionCable delivers the new booking to the grid automatically
  } else {
    error.value = res.response?.message || 'Could not save booking.'
  }
}

async function submitExternal(startAt, endAt) {
  // External (personal calendar) events — to be fully wired in Phase 8
  error.value = 'External calendar events will be available soon.'
}

defineExpose({ form })
</script>

<style scoped>
.slide-over-enter-active { transition: transform 0.25s ease; }
.slide-over-leave-active { transition: transform 0.20s ease; }
.slide-over-enter-from,
.slide-over-leave-to    { transform: translateX(100%); }

.fade-enter-active { transition: opacity 0.2s ease; }
.fade-leave-active { transition: opacity 0.15s ease; }
.fade-enter-from,
.fade-leave-to     { opacity: 0; }
</style>
